import { hostOf, isSameSite } from '../lib/sameSite';

/**
 * 「前端与 API 不同站」的运行时提醒。
 *
 * 会话是 httpOnly + `SameSite=Lax` 的 Cookie，跨站时浏览器不会发送它，
 * 于是会出现最容易被误解的现象：登录返回成功、界面也显示已登录，但每个请求都 401。
 *
 * 这种情况只会发生在「用 Pages 默认域名（*.pages.dev）访问、而 API 在自定义域名」时。
 * 在界面上直接说清楚，比让人对着 401 猜要省事得多。
 */
export function useCrossSiteInfo(): { mismatched: boolean; apiHost: string; pageHost: string } {
  const apiHost = hostOf(import.meta.env.VITE_API_BASE ?? '');
  const pageHost = typeof window === 'undefined' ? '' : window.location.host;

  // 本地开发走 Vite 代理（相对路径），不存在跨站问题
  if (apiHost === '' || pageHost === '') return { mismatched: false, apiHost, pageHost };

  return { mismatched: !isSameSite(window.location.hostname, apiHost), apiHost, pageHost };
}

export function CrossSiteNotice() {
  const { mismatched, apiHost, pageHost } = useCrossSiteInfo();
  if (!mismatched) return null;

  return (
    <div
      role="status"
      className="rounded-md border border-caution/50 bg-caution-soft px-3 py-2 text-[11px] leading-relaxed text-caution"
    >
      <p className="font-medium">当前域名与 API 不同站，Cookie 会话无法保持</p>
      <p className="mt-1 opacity-90">
        现在从 <code className="font-mono">{pageHost}</code> 访问，而 API 在{' '}
        <code className="font-mono">{apiHost}</code>。会话 Cookie 是{' '}
        <code className="font-mono">SameSite=Lax</code>，浏览器在跨站请求时不会发送它，
        所以登录看起来成功、后续请求却全部是 401。请改用与 API 同站的域名访问 —— 本项目是{' '}
        <code className="font-mono">staff.havorix.cn</code>（与{' '}
        <code className="font-mono">api.staff.havorix.cn</code> 同属 havorix.cn）。
      </p>
    </div>
  );
}
